import React, { type JSX, type ReactNode } from 'react';

import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import SettingsFeatureIconBox from './SettingsFeatureIconBox';

export type SettingsFeatureToggleRowProps = {
  id: string;
  icon: ReactNode;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  showDivider?: boolean;
  showBetaChip?: boolean;
  onChange: (checked: boolean) => void;
};

const SettingsFeatureToggleRow = ({
  id,
  icon,
  title,
  description,
  checked,
  disabled = false,
  showDivider = false,
  showBetaChip = false,
  onChange,
}: SettingsFeatureToggleRowProps): JSX.Element => {
  return (
    <>
      {showDivider ? <Divider /> : null}
      <Stack
        direction='row'
        alignItems='flex-start'
        spacing={3}
        sx={{
          py: 2,
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <SettingsFeatureIconBox size='small'>{icon}</SettingsFeatureIconBox>

        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
            <Typography id={`${id}-title`} variant='paragraphMedium' component='div'>
              {title}
            </Typography>
            {showBetaChip ? (
              <Chip label='Beta' variant='tonal' color='warning' size='small' sx={{ height: 22 }} />
            ) : null}
          </Stack>
          <Typography
            id={`${id}-description`}
            variant='captionRegular'
            color='text.secondary'
            component='p'
            sx={{ m: 0, lineHeight: '16px' }}
          >
            {description}
          </Typography>
        </Stack>

        <Stack direction='row' alignItems='center' spacing={1} sx={{ flexShrink: 0 }}>
          <Typography
            id={`${id}-status`}
            variant='captionMedium'
            color={checked && !disabled ? 'text.primary' : 'text.disabled'}
            sx={{ minWidth: '2ch' }}
          >
            {checked ? 'On' : 'Off'}
          </Typography>
          <Switch
            id={id}
            checked={checked}
            disabled={disabled}
            onChange={(_event, nextChecked) => onChange(nextChecked)}
            inputProps={{
              'aria-labelledby': `${id}-title`,
              'aria-describedby': `${id}-description ${id}-status`,
            }}
          />
        </Stack>
      </Stack>
    </>
  );
};

export default SettingsFeatureToggleRow;
